/**
 * MissionControl — Dashboard globale superadmin TotalKlick.
 * Punto di accesso unico: KPI live, tile per ogni prodotto, Cmd+K search,
 * SSO bridge verso siti esterni dell'ecosistema.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Search, Command, ArrowRight, Crown, Sparkles, Building2, Megaphone, Globe,
    TrendingUp, Users, Radio, ExternalLink, Plus, LogOut, RefreshCw, X, Wallet,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const fmtEur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

export default function MissionControl() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchOpen, setSearchOpen] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/superadmin/mc/stats");
            setStats(data);
        } catch (e) { setError(formatApiError(e)); }
        setLoading(false);
    }, []);

    useEffect(() => {
        document.title = "Mission Control · TotalKlick";
        load();
        // Cmd+K opener
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setSearchOpen(true);
            }
            if (e.key === "Escape") setSearchOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [load]);

    return (
        <div className="min-h-screen" style={{ background: "#05050F", color: "#fff", fontFamily: "Geist, Outfit, sans-serif" }} data-testid="mission-control-page">
            {/* Ambient bg */}
            <div aria-hidden style={{
                position: "fixed", inset: 0, pointerEvents: "none",
                background: "radial-gradient(circle at 15% 5%, rgba(79,139,255,0.12) 0%, transparent 45%), radial-gradient(circle at 90% 90%, rgba(255,26,92,0.08) 0%, transparent 45%)",
            }} />

            {/* Top bar */}
            <header className="relative px-6 py-5 flex items-center justify-between border-b border-white/[0.06]">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl grid place-items-center" style={{ background: "linear-gradient(135deg,#FF1A5C,#A855F7,#4F8BFF)" }}>
                        <Radio size={16} color="#fff" />
                    </div>
                    <div>
                        <div className="text-white text-[15px]" style={{ fontWeight: 500, letterSpacing: "-0.01em" }}>Mission Control</div>
                        <div className="text-white/45 text-[10.5px] uppercase tracking-[0.22em]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                            TotalKlick · Holding OS
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => setSearchOpen(true)}
                        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] text-white/75 transition border border-white/[0.08]"
                        data-testid="open-search"
                    >
                        <Search size={13} />
                        <span className="text-[12.5px]">Cerca ovunque</span>
                        <kbd className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/65 ml-1" style={{ fontFamily: "JetBrains Mono, monospace" }}>⌘K</kbd>
                    </button>
                    <button
                        type="button"
                        onClick={load}
                        className="w-9 h-9 grid place-items-center rounded-xl bg-white/[0.04] hover:bg-white/[0.08] text-white/65 transition"
                        title="Aggiorna"
                        data-testid="refresh-stats"
                    >
                        <RefreshCw size={14} />
                    </button>
                    <div className="text-right hidden sm:block">
                        <div className="text-[12px] text-white">{user?.email}</div>
                        <div className="text-[10px] text-white/45 uppercase tracking-[0.22em]" style={{ fontFamily: "JetBrains Mono, monospace" }}>{user?.role}</div>
                    </div>
                    <button
                        type="button"
                        onClick={() => { logout(); navigate("/login"); }}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] text-white/75 hover:text-white hover:bg-white/[0.06] transition"
                        data-testid="logout-btn"
                    >
                        <LogOut size={12} /> Esci
                    </button>
                </div>
            </header>

            <main className="relative max-w-7xl mx-auto px-6 py-8">
                {error && (
                    <div className="mb-4 p-3 rounded-xl text-[12px]" style={{ background: "rgba(255,26,92,0.1)", color: "#FF7AA3", border: "1px solid rgba(255,26,92,0.3)" }}>
                        {error}
                    </div>
                )}

                {/* KPI ROW */}
                <KpiRow stats={stats} loading={loading} />

                {/* Sezione Prodotti Nativi */}
                <div className="mt-10 mb-4 flex items-center justify-between">
                    <h2 className="text-white text-[18px]" style={{ fontWeight: 500, letterSpacing: "-0.02em" }}>I tuoi prodotti</h2>
                    <span className="text-[10.5px] uppercase tracking-[0.22em] text-white/40" style={{ fontFamily: "JetBrains Mono, monospace" }}>controllo 100%</span>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <ProductTile
                        title="TotalPL"
                        subtitle="PrintLab tenants"
                        accent="#FF1A5C"
                        icon={Building2}
                        metrics={[
                            { label: "Tenants attivi", value: stats?.totalpl?.tenants_active ?? "—" },
                            { label: "Lead in coda", value: stats?.totalpl?.leads_pending ?? "—", highlight: (stats?.totalpl?.leads_pending || 0) > 0 },
                        ]}
                        to="/superadmin/totalpl"
                        testid="tile-totalpl"
                    />
                    <ProductTile
                        title="Aria Widget"
                        subtitle="Digital Presence Layer"
                        accent="#A855F7"
                        icon={Sparkles}
                        metrics={[
                            { label: "Istanze live", value: stats?.aria?.instances_total ?? "—" },
                            { label: "Sottoscrizioni", value: stats?.subscriptions?.total ?? "—" },
                        ]}
                        to="/superadmin/aria"
                        testid="tile-aria"
                    />
                    <ProductTile
                        title="Founders Club"
                        subtitle="Lifetime + equity"
                        accent="#FFD66B"
                        icon={Crown}
                        metrics={[
                            { label: "Founders venduti", value: stats?.founders?.count ?? "—" },
                            { label: "Posti rimasti", value: stats?.founders?.remaining ?? "—" },
                        ]}
                        to="/superadmin/founders"
                        testid="tile-founders"
                    />
                    <ProductTile
                        title="TotalKlickADS"
                        subtitle="Agenzia AI"
                        accent="#4F8BFF"
                        icon={Megaphone}
                        metrics={[
                            { label: "Brand attivi", value: stats?.ads?.brands ?? "—" },
                            { label: "Worker", value: "● live", highlight: true },
                        ]}
                        to="/totalklickads/dashboard"
                        testid="tile-ads"
                    />
                </div>

                {/* Sezione Ecosystem */}
                <div className="mt-12 mb-4 flex items-center justify-between">
                    <div>
                        <h2 className="text-white text-[18px]" style={{ fontWeight: 500, letterSpacing: "-0.02em" }}>Ecosistema esterno</h2>
                        <div className="text-[11.5px] text-white/45 mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                            Apri qualsiasi sito loggato come superadmin (SSO JWT)
                        </div>
                    </div>
                    <Link to="/superadmin/ecosystem" className="text-[12px] text-white/75 hover:text-white inline-flex items-center gap-1" data-testid="manage-ecosystem">
                        Gestisci apps <ArrowRight size={11} />
                    </Link>
                </div>

                <EcosystemRow />

                {/* Forge Quick Launch */}
                <ForgeQuickLaunch />

                {/* Quick Actions */}
                <div className="mt-12 mb-4">
                    <h2 className="text-white text-[18px]" style={{ fontWeight: 500, letterSpacing: "-0.02em" }}>Azioni rapide</h2>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <QuickAction label="Crea nuovo tenant TotalPL" to="/superadmin/totalpl?action=create-tenant" icon={Plus} accent="#FF1A5C" />
                    <QuickAction label="Esporta Founders CSV" href="/api/superadmin/mc/founders/export.csv" icon={Crown} accent="#FFD66B" />
                    <QuickAction label="Vedi widget Aria live" to="/superadmin/aria" icon={Sparkles} accent="#A855F7" />
                    <QuickAction label="Gestisci Billing Core (prodotti + piani)" to="/superadmin/billing" icon={Wallet} accent="#4F8CFF" />
                </div>
            </main>

            {searchOpen && <SearchPalette onClose={() => setSearchOpen(false)} />}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
function KpiRow({ stats, loading }) {
    const items = [
        { label: "MRR", value: fmtEur(stats?.mrr_eur), sub: `${stats?.subscriptions?.active || 0} active · ${stats?.subscriptions?.trialing || 0} trial`, icon: TrendingUp, c1: "#86EFAC", c2: "#10B981" },
        { label: "Lifetime", value: fmtEur(stats?.lifetime_revenue_eur), sub: `${stats?.founders?.count || 0} · €1.990 cad`, icon: Crown, c1: "#FFD66B", c2: "#FF6B3D" },
        { label: "Aria live", value: stats?.aria?.instances_total ?? "—", sub: `${stats?.aria?.instances_paid || 0} paid`, icon: Sparkles, c1: "#A855F7", c2: "#7C3AED" },
        { label: "TotalPL", value: stats?.totalpl?.tenants_active ?? "—", sub: `${stats?.totalpl?.leads_pending || 0} lead in coda`, icon: Building2, c1: "#FF7AA3", c2: "#FF1A5C" },
    ];
    return (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-row">
            {items.map((it, i) => (
                <div
                    key={i}
                    className="rounded-2xl p-5 relative overflow-hidden"
                    style={{ background: "rgba(15,18,28,0.6)", border: "1px solid rgba(255,255,255,0.06)", backdropFilter: "blur(20px)" }}
                    data-testid={`kpi-${it.label.toLowerCase().replace(/ /g, '-')}`}
                >
                    <div className="flex items-start justify-between mb-3">
                        <div className="text-[10.5px] uppercase tracking-[0.25em] text-white/55" style={{ fontFamily: "JetBrains Mono, monospace" }}>{it.label}</div>
                        <div className="w-7 h-7 rounded-lg grid place-items-center" style={{ background: `linear-gradient(135deg, ${it.c1}30, ${it.c2}10)`, border: `1px solid ${it.c1}40` }}>
                            <it.icon size={12} style={{ color: it.c1 }} />
                        </div>
                    </div>
                    <div className="text-white text-[28px]" style={{ fontWeight: 200, letterSpacing: "-0.025em" }}>
                        {loading ? <span className="text-white/30">…</span> : it.value}
                    </div>
                    <div className="text-[11.5px] text-white/55 mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>{it.sub}</div>
                    <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none", background: `radial-gradient(circle at 100% 0%, ${it.c1}10, transparent 60%)` }} />
                </div>
            ))}
        </div>
    );
}

function ProductTile({ title, subtitle, accent, icon: Icon, metrics, to, testid }) {
    return (
        <Link
            to={to}
            className="group relative rounded-2xl p-5 transition hover:bg-white/[0.04] block"
            style={{ background: "rgba(15,18,28,0.6)", border: "1px solid rgba(255,255,255,0.06)", backdropFilter: "blur(20px)" }}
            data-testid={testid}
        >
            <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl grid place-items-center flex-none" style={{ background: `linear-gradient(135deg, ${accent}30, ${accent}10)`, border: `1px solid ${accent}40` }}>
                    <Icon size={15} style={{ color: accent }} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-white text-[15px]" style={{ fontWeight: 500 }}>{title}</div>
                    <div className="text-white/50 text-[11.5px]" style={{ fontFamily: "Outfit, sans-serif" }}>{subtitle}</div>
                </div>
                <ArrowRight size={14} className="text-white/30 group-hover:text-white group-hover:translate-x-0.5 transition flex-none" />
            </div>
            <div className="space-y-1.5">
                {metrics.map((m, i) => (
                    <div key={i} className="flex items-center justify-between text-[12.5px]">
                        <span className="text-white/55" style={{ fontFamily: "Outfit, sans-serif" }}>{m.label}</span>
                        <span style={{ color: m.highlight ? accent : "#fff", fontWeight: m.highlight ? 600 : 400, fontFamily: "JetBrains Mono, monospace" }}>{m.value}</span>
                    </div>
                ))}
            </div>
        </Link>
    );
}

function QuickAction({ label, to, href, icon: Icon, accent }) {
    const className = "group relative rounded-xl p-4 flex items-center gap-3 transition hover:bg-white/[0.04]";
    const style = { background: "rgba(15,18,28,0.55)", border: "1px solid rgba(255,255,255,0.06)" };
    const inner = (
        <>
            <div className="w-8 h-8 rounded-lg grid place-items-center flex-none" style={{ background: `linear-gradient(135deg, ${accent}30, ${accent}10)`, border: `1px solid ${accent}40` }}>
                <Icon size={13} style={{ color: accent }} />
            </div>
            <span className="flex-1 text-white text-[13px]">{label}</span>
            <ArrowRight size={12} className="text-white/30 group-hover:text-white transition flex-none" />
        </>
    );
    if (href) return <a href={href} target="_blank" rel="noreferrer" className={className} style={style} data-testid={`quick-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>{inner}</a>;
    return <Link to={to} className={className} style={style} data-testid={`quick-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>{inner}</Link>;
}

// ─────────────────────────────────────────────────────────────────────────────
function EcosystemRow() {
    const [apps, setApps] = useState([]);
    const [launching, setLaunching] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/superadmin/mc/ecosystem/apps");
                setApps(data.items || []);
            } catch { /* silent */ }
        })();
    }, []);

    const launchSSO = async (app) => {
        if (app.placeholder) {
            alert(`${app.display_name} non è ancora deployato. Quando avrai il dominio, aggiorna l'URL di callback in "Gestisci apps".`);
            return;
        }
        setLaunching(app.app_key);
        try {
            // Crea ticket SSO via endpoint esistente sso/authorize
            const { data } = await api.get(`/auth/sso/authorize?app_key=${app.app_key}&role=superadmin`);
            const cb = (app.return_urls || [])[0];
            if (data?.sso_token && cb) {
                window.open(`${cb}?token=${data.sso_token}`, "_blank", "noopener");
            } else {
                alert("SSO ticket non disponibile per questa app");
            }
        } catch (e) {
            alert(`Impossibile aprire ${app.display_name}: ${formatApiError(e)}`);
        }
        setLaunching(null);
    };

    return (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ecosystem-row">
            {apps.map((a) => (
                <button
                    key={a.app_key}
                    type="button"
                    onClick={() => launchSSO(a)}
                    disabled={launching === a.app_key}
                    className="text-left rounded-2xl p-4 relative overflow-hidden transition hover:bg-white/[0.04] disabled:opacity-60"
                    style={{ background: "rgba(15,18,28,0.55)", border: `1px solid ${a.placeholder ? "rgba(255,255,255,0.06)" : a.accent + "40"}` }}
                    data-testid={`ecosystem-${a.app_key}`}
                >
                    <div className="flex items-start gap-3 mb-2">
                        <div className="w-9 h-9 rounded-xl grid place-items-center flex-none" style={{ background: `linear-gradient(135deg, ${a.accent}30, ${a.accent}10)`, border: `1px solid ${a.accent}40` }}>
                            <Globe size={13} style={{ color: a.accent }} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                                <span className="text-white text-[14px]" style={{ fontWeight: 500 }}>{a.display_name}</span>
                                {a.placeholder && <span className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-[0.2em]" style={{ background: "rgba(255,255,255,0.06)", color: "#FFD66B", fontFamily: "JetBrains Mono, monospace" }}>placeholder</span>}
                                {a.multi_brand_domains && <span className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-[0.2em]" style={{ background: "rgba(168,85,247,0.15)", color: "#C7A4FF", fontFamily: "JetBrains Mono, monospace" }}>multi-brand</span>}
                            </div>
                            <div className="text-white/50 text-[11.5px] truncate" style={{ fontFamily: "JetBrains Mono, monospace" }}>{a.domain}</div>
                        </div>
                        {launching === a.app_key
                            ? <RefreshCw size={13} className="text-white/55 animate-spin flex-none" />
                            : <ExternalLink size={12} className="text-white/30 flex-none" />}
                    </div>
                    <div className="text-[11px] text-white/45 uppercase tracking-[0.2em]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                        {a.category}
                    </div>
                </button>
            ))}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
function SearchPalette({ onClose }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (q.length < 2) { setResults([]); return; }
        let cancelled = false;
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get(`/superadmin/mc/search?q=${encodeURIComponent(q)}`);
                if (!cancelled) setResults(data.results || []);
            } catch { if (!cancelled) setResults([]); }
            if (!cancelled) setLoading(false);
        }, 250);
        return () => { cancelled = true; clearTimeout(t); };
    }, [q]);

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4" style={{ background: "rgba(5,5,15,0.78)", backdropFilter: "blur(8px)" }} onClick={onClose} data-testid="search-palette">
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-2xl rounded-2xl overflow-hidden"
                style={{ background: "#0d1018", border: "1px solid rgba(255,255,255,0.1)", boxShadow: "0 40px 100px -20px rgba(79,139,255,0.25)" }}
            >
                <div className="flex items-center gap-3 px-4 py-3.5 border-b border-white/[0.06]">
                    <Command size={14} className="text-white/55" />
                    <input
                        autoFocus
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Cerca tenant, utente, lead, founder, widget Aria, sito esterno…"
                        className="flex-1 bg-transparent text-white placeholder-white/35 outline-none text-[14px]"
                        data-testid="search-input"
                    />
                    <button type="button" onClick={onClose} className="w-7 h-7 grid place-items-center rounded-lg text-white/55 hover:text-white hover:bg-white/[0.06]" data-testid="close-search">
                        <X size={13} />
                    </button>
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                    {loading && <div className="px-5 py-6 text-white/45 text-[12.5px]">Cerco…</div>}
                    {!loading && q.length >= 2 && results.length === 0 && (
                        <div className="px-5 py-6 text-white/45 text-[12.5px]">Nessun risultato per &quot;{q}&quot;</div>
                    )}
                    {!loading && q.length < 2 && (
                        <div className="px-5 py-6 text-white/45 text-[12.5px]">Digita almeno 2 caratteri</div>
                    )}
                    {results.map((r, i) => (
                        <Link
                            key={i}
                            to={r.href}
                            onClick={onClose}
                            className="flex items-center gap-3 px-4 py-3 hover:bg-white/[0.04] transition border-t border-white/[0.04]"
                            data-testid={`search-result-${r.type}`}
                        >
                            <span className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-[0.2em]" style={{ background: "rgba(255,255,255,0.05)", color: "#9EC3FF", fontFamily: "JetBrains Mono, monospace" }}>{r.type}</span>
                            <div className="flex-1 min-w-0">
                                <div className="text-white text-[13px] truncate">{r.title}</div>
                                <div className="text-white/50 text-[11.5px] truncate" style={{ fontFamily: "Outfit, sans-serif" }}>{r.subtitle}</div>
                            </div>
                            <ArrowRight size={12} className="text-white/35" />
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════
// ForgeQuickLaunch — accesso rapido a FORGE Cognitive OS con URL editabile.
// Same-origin di default (/forge). Modificabile e persistito in localStorage.
// ═══════════════════════════════════════════════════════════════════════
function ForgeQuickLaunch() {
    const STORAGE_KEY = "superadmin:forge_url";
    const DEFAULT_URL = "/forge";
    const [url, setUrl] = useState(() => {
        try { return localStorage.getItem(STORAGE_KEY) || DEFAULT_URL; }
        catch (e) { return DEFAULT_URL; }
    });
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(url);

    const save = () => {
        const clean = (draft || "").trim() || DEFAULT_URL;
        try { localStorage.setItem(STORAGE_KEY, clean); } catch (e) { /* ignore */ }
        setUrl(clean);
        setEditing(false);
    };
    const reset = () => {
        try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
        setUrl(DEFAULT_URL); setDraft(DEFAULT_URL); setEditing(false);
    };
    const openForge = () => {
        if (url.startsWith("http://") || url.startsWith("https://")) {
            window.open(url, "_blank", "noopener,noreferrer");
        } else {
            window.location.href = url;
        }
    };

    return (
        <div
            className="mt-8 rounded-2xl p-5 relative overflow-hidden"
            style={{
                background: "linear-gradient(135deg, rgba(139,92,246,0.15), rgba(34,211,238,0.08))",
                border: "1px solid rgba(139,92,246,0.3)",
                backdropFilter: "blur(20px)",
            }}
            data-testid="forge-quick-launch"
        >
            <div className="flex items-start gap-4 mb-4">
                <div
                    className="w-11 h-11 rounded-xl grid place-items-center shrink-0"
                    style={{ background: "linear-gradient(135deg, #8b5cf6, #22d3ee)", boxShadow: "0 8px 20px rgba(139,92,246,0.4)" }}
                >
                    <Sparkles size={20} style={{ color: "#fff" }} />
                </div>
                <div className="flex-1">
                    <h3 className="text-white text-[16px]" style={{ fontWeight: 600, letterSpacing: "-0.01em" }}>
                        FORGE · Cognitive OS
                    </h3>
                    <p className="text-[13px] mt-0.5" style={{ color: "rgba(255,255,255,0.6)" }}>
                        Accesso rapido a Forge. L&#39;URL è editabile — punta all&#39;installazione locale (<code style={{ color: "#22d3ee" }}>/forge</code>) o a una VPS esterna.
                    </p>
                </div>
            </div>

            {!editing ? (
                <div className="flex items-center gap-2 flex-wrap">
                    <div
                        className="flex-1 min-w-[200px] px-3 py-2 rounded-lg text-[13px] font-mono"
                        style={{ background: "rgba(15,18,28,0.6)", color: "#e2e8f0", border: "1px solid rgba(255,255,255,0.08)" }}
                        data-testid="forge-quick-launch-url"
                    >
                        {url}
                    </div>
                    <button
                        onClick={openForge}
                        data-testid="forge-quick-launch-open"
                        className="px-4 py-2 rounded-lg text-white text-[13px] font-semibold flex items-center gap-2 hover:opacity-90 transition"
                        style={{ background: "linear-gradient(135deg, #8b5cf6, #22d3ee)" }}
                    >
                        <ArrowRight size={14} /> Apri Forge
                    </button>
                    <button
                        onClick={() => { setDraft(url); setEditing(true); }}
                        data-testid="forge-quick-launch-edit"
                        className="px-3 py-2 rounded-lg text-[13px] font-semibold hover:bg-white/5 transition"
                        style={{ border: "1px solid rgba(255,255,255,0.15)", color: "#e2e8f0" }}
                    >
                        ✎ Modifica URL
                    </button>
                </div>
            ) : (
                <div className="flex items-center gap-2 flex-wrap">
                    <input
                        type="text"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
                        placeholder="/forge  oppure  https://forge.tuodominio.com"
                        data-testid="forge-quick-launch-input"
                        autoFocus
                        className="flex-1 min-w-[200px] px-3 py-2 rounded-lg text-[13px] font-mono outline-none"
                        style={{ background: "rgba(15,18,28,0.6)", color: "#fff", border: "1px solid #8b5cf6" }}
                    />
                    <button
                        onClick={save}
                        data-testid="forge-quick-launch-save"
                        className="px-4 py-2 rounded-lg text-white text-[13px] font-semibold hover:opacity-90 transition"
                        style={{ background: "#10b981" }}
                    >
                        ✓ Salva
                    </button>
                    <button
                        onClick={() => setEditing(false)}
                        className="px-3 py-2 rounded-lg text-[13px] font-semibold hover:bg-white/5 transition"
                        style={{ border: "1px solid rgba(255,255,255,0.15)", color: "#e2e8f0" }}
                    >
                        Annulla
                    </button>
                    <button
                        onClick={reset}
                        data-testid="forge-quick-launch-reset"
                        className="px-3 py-2 rounded-lg text-[13px] font-semibold hover:bg-red-500/10 transition"
                        style={{ border: "1px solid rgba(255,255,255,0.15)", color: "#f87171" }}
                        title="Ripristina URL di default"
                    >
                        ↺ Reset
                    </button>
                </div>
            )}
        </div>
    );
}
